import React from "react";
import utilities from "./UtilityFunctions";

// ─────────────────────────────────────────────────────────────────────────
// Inline cell visuals: zero-dependency micro-graphics rendered *beside* /
// *under* the formatted value inside table cells (never visual-only).
// Semantic data colors are deliberately hardcoded (matching AGING_COLORS),
// NOT tenant-themed — see the design-system decision.
// ─────────────────────────────────────────────────────────────────────────

// Visuals are intentionally restricted to these two sheets: Cash/Cancelled
// rows contain the same fields but the context is different and could mislead.
export const VISUAL_SHEETS = new Set(["Credit", "CreditCleared"]);

// Per-field override of the sheet allowlist above. ListingCompleteness is the
// first visual that belongs somewhere other than the credit sheets, and putting
// Inventory into VISUAL_SHEETS to get it would have switched on the balance and
// payment-score graphics there too — the exact "same field, different context"
// confusion the restriction exists to prevent. Fields absent from this map fall
// back to VISUAL_SHEETS.
const FIELD_VISUAL_SHEETS = {
    ListingCompleteness: new Set(["Inventory"]),
};

// The single gate every renderer path should ask. Returns the renderer only if
// this field's visual is allowed on this sheet, else undefined (plain text).
export function visualFor(fieldName, sheetName) {
    if (!CELL_VISUALS[fieldName]) return undefined;
    const allowed = FIELD_VISUAL_SHEETS[fieldName] || VISUAL_SHEETS;
    return allowed.has(sheetName) ? CELL_VISUALS[fieldName] : undefined;
}

const GREEN = "#16a34a";
const ORANGE = "#ea580c";
const RED = "#dc2626";
const TRACK = "#e5e7eb"; // neutral bar/dial track
const MUTED = "#64748b";

// Thresholds — single source of truth, easy to tune ----------------------
const PACE = { red: 50, orange: 85 };      // score/pace: <50 red, 50–84.99 orange, ≥85 green
const AVGPACE = { red: 50, orange: 75 };   // avg-payment ratio: <50 red, 50–74.99 orange, ≥75 green
const PROG = { red: 34, orange: 67 };      // progress %: thirds
const OVERDUE = { red: 31 };               // ≤0 green (not yet due), 1–30 orange, >30 red
const LASTPMT = { green: 35, orange: 65 }; // days since last payment; null → gray

// Normalized records store NULL numerics as "" (see UtilityFunctions.formatFromInput),
// so "no value" is null | undefined | "".
const isBlank = (v) => v === null || v === undefined || v === "";

const clamp = (n, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, n));

const paceColor = (pct) => (pct < PACE.red ? RED : pct < PACE.orange ? ORANGE : GREEN);
const avgColor = (pct) => (pct < AVGPACE.red ? RED : pct < AVGPACE.orange ? ORANGE : GREEN);
const progColor = (pct) => (pct < PROG.red ? RED : pct < PROG.orange ? ORANGE : GREEN);
const overdueColor = (days) => (days <= 0 ? GREEN : days < OVERDUE.red ? ORANGE : RED);
const lastPmtColor = (days) =>
    days == null ? MUTED : days < LASTPMT.green ? GREEN : days < LASTPMT.orange ? ORANGE : RED;

const daysSince = (dateVal) => {
    if (!dateVal) return null;
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const numDecimal = (v) => utilities.formatIntoInput(v, "decimal");

// Micro-components ---------------------------------------------------------
// THE TRACK'S WIDTH LIVES IN CSS (`.mini-bar`, carstyles.css), not here. It is
// 72px everywhere by default, but a bar sitting in a full-width phone-card cell
// stretches to fill it — and an inline width would beat that rule. Everything
// else stays inline because it is either constant or driven by `color`.
export function MiniBar({ pct, color }) {
    return (
        <div className="mini-bar" style={{ height: 4, borderRadius: 2, background: TRACK, marginTop: 2, overflow: "hidden" }}>
            <div style={{ width: `${clamp(pct)}%`, height: "100%", background: color, borderRadius: 2 }} />
        </div>
    );
}

// Donut with the percentage figure centered inside (e.g. "80%"). `value` drives
// both the arc length (clamped 0–100) and the label; `color` (optional) tints
// the arc + label, defaulting to the performance-score ramp. The real value may
// exceed 100 (a fast payer) — the arc caps at full but the label shows the true %.
export function MiniDial({ value, color, size = 40 }) {
    const pct = clamp(value);
    const stroke = 3.5;
    const r = (size - stroke) / 2 - 0.5;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    const mid = size / 2;
    const arcColor = color || paceColor(value);
    const label = `${Math.round(value)}%`;
    // Shrink the font a touch so 4+ digit percentages still fit inside the ring.
    const fontSize = label.length >= 5 ? 8 : label.length === 4 ? 9 : 10;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block", flexShrink: 0 }} role="img" aria-label={label}>
            <circle cx={mid} cy={mid} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
            <circle
                cx={mid}
                cy={mid}
                r={r}
                fill="none"
                stroke={arcColor}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={`${dash} ${circ}`}
                transform={`rotate(-90 ${mid} ${mid})`}
            />
            <text x={mid} y={mid} textAnchor="middle" dominantBaseline="central" fontSize={fontSize} fontWeight={600} fill={arcColor}>
                {label}
            </text>
        </svg>
    );
}

export function Dot({ color }) {
    return (
        <span
            style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "9999px",
                background: color,
                marginRight: 6,
                flexShrink: 0,
            }}
        />
    );
}

export function NotStartedPill() {
    return <span className="pill-t pill-t-muted">Not started</span>;
}

// Registry — the ONLY contract CommonTBody / mobile cards need.
// Signature: (value, row, sheetName) => ReactNode | null   (null = fall back to plain text)
// Every renderer must be null-safe on missing row fields (Cash/Committed rows
// lack most of these): return null and the plain-text path takes over.
export const CELL_VISUALS = {
    // Payments made toward the credit, as a fill of total credit owed.
    TotalARPayments: (v, r) => {
        if (isBlank(v) || !(r && r.TotalCredit > 0)) return null;
        const pct = clamp((v / r.TotalCredit) * 100);
        return (
            <div>
                <div>{numDecimal(v)}</div>
                <MiniBar pct={pct} color={progColor(pct)} />
            </div>
        );
    },

    // Outstanding balance as a fill of the total credit owed. Bar DRAINS: full
    // balance = full red bar; near-zero balance = short green bar.
    TotalBalance: (v, r) => {
        if (isBlank(v) || !(r && r.TotalCredit > 0)) return null;
        const fill = clamp((v / r.TotalCredit) * 100);
        return (
            <div>
                <div>{numDecimal(v)}</div>
                <MiniBar pct={fill} color={progColor(100 - fill)} />
            </div>
        );
    },

    // Total paid as a fill of the agreed vehicle price (progress: more = greener).
    TotalPaid: (v, r) => {
        if (isBlank(v) || !(r && r.AgreedPrice > 0)) return null;
        const pct = clamp((v / r.AgreedPrice) * 100);
        return (
            <div>
                <div>{numDecimal(v)}</div>
                <MiniBar pct={pct} color={progColor(pct)} />
            </div>
        );
    },

    // Performance score dial with the score% inside. NULL (deal < 1 month old)
    // → "Not started" pill; but on CreditCleared a null score means the deal
    // ended before a score was calculated — show a muted dash instead.
    PaymentPerformanceScore: (v, _row, sheetName) =>
        isBlank(v)
            ? sheetName === "CreditCleared"
                ? <span style={{ color: MUTED }}>—</span>
                : <NotStartedPill />
            : <MiniDial value={Number(v)} color={paceColor(Number(v))} />,

    // Avg monthly payment: a donut of avg-vs-expected ratio (own 50/75 ramp,
    // figure inside) next to the black amount (no decimals).
    AvgMonthlyPayment: (v, r) => {
        if (isBlank(v)) return <span style={{ color: MUTED }}>—</span>;
        const amount = numDecimal(Math.round(Number(v)));
        const mp = r && r.MonthlyPayment;
        if (!(mp > 0)) return <span>{amount}</span>;
        const ratio = (Number(v) / mp) * 100;
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <MiniDial value={ratio} color={avgColor(ratio)} />
                <span>{amount}</span>
            </span>
        );
    },

    // Days overdue: colored dot before the value. Negative = not yet due = green.
    DaysOverdue: (v) => {
        if (isBlank(v)) return null;
        return (
            <span style={{ display: "inline-flex", alignItems: "center" }}>
                <Dot color={overdueColor(Number(v))} />
                {utilities.formatIntoInput(v, "integer")}
            </span>
        );
    },

    // How much of the listing a buyer on the public website can filter on.
    // Uses the shared PROG ramp rather than a new one: the lookup can carry a
    // typical vehicle to the high seventies, so a green band starting at 67 is
    // a bar that can actually be cleared. A threshold no car can reach would
    // paint the whole column red permanently, and a column that is always red
    // stops being read — the same reasoning that set the placeholder-chassis
    // thresholds at 8-and-5 rather than the more obvious 6-and-4.
    ListingCompleteness: (v) => {
        if (isBlank(v)) return null;
        const pct = clamp(Number(v));
        return (
            <div>
                <div style={{ color: pct >= 100 ? MUTED : undefined }}>{`${Math.round(pct)}%`}</div>
                <MiniBar pct={pct} color={progColor(pct)} />
            </div>
        );
    },

    // Last payment date: dot ramps on days-since; null date → gray dot + blank.
    LastPmtDate: (v, r) => {
        if (!r || !("LastPmtDate" in r)) return null;
        const days = daysSince(v);
        return (
            <span style={{ display: "inline-flex", alignItems: "center" }}>
                <Dot color={lastPmtColor(days)} />
                {utilities.formatIntoInput(v, "date")}
            </span>
        );
    },
};

// Exposed for the mobile card branch (CommonDataView) so it shares the exact
// same math/colors without re-deriving them. `paceColor` is there because the
// phone draws the performance score as a bar rather than the dial above — the
// shape changes, the ramp must not.
export { progColor, paceColor, lastPmtColor, MUTED };
